import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import ProtectedRoute from './ProtectedRoute';
import DashboardLayout from './DashboardLayout';
import { API_BASE, SUPER_ADMIN_EMAIL } from '../config';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { createNavigateWrapper } from '../utils/navigation';
import { useTranslation } from '../i18n/LanguageContext';
import { Home, History, RefreshCw, Clock, MapPin, Calendar, Camera, UserCheck, AlertTriangle, X, PanelRightOpen } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import HomePage from './HomePage';
import GeolocationAttendance from './GeolocationAttendance';
import GeolocationAdminPanel from './GeolocationAdminPanel';
import AttendanceAudit from './AttendanceAudit';
import AdminDashboard from './AdminDashboard';
import Employees from './Employees';
import EmployeesImport from './EmployeesImport';
import ReportGenerator from './Reports/ReportGenerator';
import LeavesManagement from './Leaves/LeavesManagement';
import BalanceManagement from './BalanceManagement';
import BadgeManagement from './BadgeManagement';
import FormationsManagement from './FormationsManagement';
import NotesFraisManagement from './NotesFraisManagement';
import PayrollRuns from './Payroll/PayrollRuns';
import PerformanceDashboard from './Performance/PerformanceDashboard';
import PersonalCalendar from './Calendar/PersonalCalendar';
import TeamCalendar from './Planning/TeamCalendar';
import IdeasList from './Ideas/IdeasList';
import IdeasNew from './Ideas/IdeasNew';
import IdeasDetail from './Ideas/IdeasDetail';
import AnnouncementsManager from './Announcements/AnnouncementsManager';
import BillingDashboard from './BillingDashboard';
import BillingPlans from './BillingPlans';
import BillingSuccess from './BillingSuccess';
import UserProfile from './UserProfile';
import HelpPage from './HelpPage';
import DocumentsPage from './DocumentsPage';
import AdminPanel from './AdminPanel';
import AdminDashboardHome from './AdminDashboardHome';
import SuperAdminPanel from './SuperAdminPanel';
import LiveChat from './LiveChat/LiveChat';
import PushDebugPage from './Push/PushDebugPage';
const NotificationCenter = lazy(() =>
  import('./Notifications/NotificationCenter')
    .then((m) => ({ default: m.default || m.NotificationCenter || (() => <div style={{ padding: 24, color: '#fff' }}>Notification Center</div>) }))
    .catch(() => ({ default: () => <div style={{ padding: 24, color: '#fff' }}>Notification Center unavailable</div> }))
);

// Composant AttendanceHistory avec design moderne et responsive
const AttendanceHistory = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedPhoto, setSelectedPhoto] = React.useState(null);
  const [filterStartDate, setFilterStartDate] = React.useState('');
  const [filterType, setFilterType] = React.useState('all'); // 'all' | 'in' | 'out'
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [historyDrawerOpen, setHistoryDrawerOpen] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const checkMobile = () => {
      setIsMobile(mql.matches);
      if (!mql.matches) setHistoryDrawerOpen(false);
    };
    checkMobile();
    mql.addEventListener('change', checkMobile);
    return () => mql.removeEventListener('change', checkMobile);
  }, []);

  React.useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStartDate) {
        params.set('startDate', filterStartDate);
        params.set('endDate', filterStartDate);
      }
      if (filterType === 'in' || filterType === 'out') params.set('clockType', filterType);
      const qs = params.toString();
      const url = qs ? `${API_BASE}/api/attendance/history?${qs}` : `${API_BASE}/api/attendance/history`;
      const response = await fetchWithTimeout(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }, 15000);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📋 [HISTORIQUE] Réponse API:', data);
        
        // Le backend retourne une réponse paginée avec:
        // - data.data: les enregistrements paginés
        // - data.groupedByDate: les enregistrements groupés par date (format attendu par le frontend)
        // - data.history: format alternatif (pour compatibilité)
        // - data.records: format alternatif (pour compatibilité)
        
        if (data.groupedByDate) {
          // Convertir groupedByDate (objet) en tableau de groupes
          const groupedArray = Object.values(data.groupedByDate).sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          );
          setHistory(groupedArray);
        } else if (data.data && Array.isArray(data.data)) {
          // Si on a data.data (format paginé), on doit regrouper par date
          const groupedByDate = {};
          data.data.forEach(record => {
            const dateKey = record.date || new Date(record.timestamp).toISOString().split('T')[0];
            if (!groupedByDate[dateKey]) {
              groupedByDate[dateKey] = {
                date: dateKey,
                records: []
              };
            }
            groupedByDate[dateKey].records.push(record);
          });
          setHistory(Object.values(groupedByDate).sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          ));
        } else if (data.history && Array.isArray(data.history)) {
          setHistory(data.history);
        } else if (data.records && Array.isArray(data.records)) {
          // Regrouper par date si c'est un tableau simple
          const groupedByDate = {};
          data.records.forEach(record => {
            const dateKey = record.date || new Date(record.timestamp).toISOString().split('T')[0];
            if (!groupedByDate[dateKey]) {
              groupedByDate[dateKey] = {
                date: dateKey,
                records: []
              };
            }
            groupedByDate[dateKey].records.push(record);
          });
          setHistory(Object.values(groupedByDate).sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          ));
        } else {
          console.warn('📋 [HISTORIQUE] Format de réponse non reconnu:', data);
          setHistory([]);
        }
      } else {
        console.error('📋 [HISTORIQUE] Erreur HTTP:', response.status, response.statusText);
        setHistory([]);
      }
    } catch (error) {
      console.error('📋 [HISTORIQUE] Erreur chargement historique:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // Format d'affichage des données groupées par date
  // Le format attendu est un tableau de groupes, chaque groupe ayant:
  // { date: "YYYY-MM-DD", records: [...] }
  const displayData = React.useMemo(() => {
    if (!history || history.length === 0) {
      return [];
    }
    
    // Si history est déjà au format attendu (tableau de groupes avec date et records)
    if (Array.isArray(history) && history.length > 0 && history[0].date && history[0].records) {
      return history;
    }
    
    // Sinon, on regroupe par date (format de compatibilité)
    const grouped = {};
    history.forEach((record) => {
      const dateKey = record.date || new Date(record.timestamp).toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          records: []
        };
      }
      grouped[dateKey].records.push(record);
    });
    return Object.values(grouped).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [history]);

  return (
    <div style={{
      minHeight: '100vh',
      height: '100%',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      <style>{`
        .history-filter-date-input::-webkit-calendar-picker-indicator {
          filter: invert(1);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>
      {/* Header: on mobile = compact bar + drawer; subtitle & filters in content. Desktop = all in fixed. */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: isMobile ? '12px 16px' : '20px clamp(16px, 4vw, 40px)',
        background: 'rgba(10, 14, 39, 0.8)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1000
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          minWidth: 0
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          {isMobile ? (
            <>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <History size={22} style={{ flexShrink: 0 }} />
                {t('attendance.history_title') || t('common.history')}
              </div>
              <button
                type="button"
                onClick={() => setHistoryDrawerOpen(true)}
                aria-label={t('home.open_menu') || 'Menu'}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <PanelRightOpen size={20} />
              </button>
            </>
          ) : (
            <>
              <div style={{
                fontSize: 'clamp(28px, 5vw, 40px)',
                fontWeight: 700,
                maxWidth: '100%',
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <History size={32} />
                {t('attendance.history_title') || t('common.history')}
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <>
            <p style={{
              fontSize: 'clamp(14px, 2vw, 16px)',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: '8px 0 0 0',
              letterSpacing: '0.2px',
              fontWeight: 500,
              maxWidth: '1200px',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: onNavigate ? '64px' : '0'
            }}>
              {t('attendance.history_subtitle') || t('attendance.history')}
            </p>
          </>
        )}
      </div>

      {/* Mobile drawer */}
      {isMobile && historyDrawerOpen && (
        <div role="presentation" onClick={() => setHistoryDrawerOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'flex-end' }}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} style={{ width: 'min(320px, 88vw)', height: '100%', background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)', padding: '20px 16px', boxSizing: 'border-box', overflowY: 'auto', borderLeft: '1px solid rgba(255,255,255,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>{t('attendance.history_title') || t('common.history')}</span>
              <button type="button" onClick={() => setHistoryDrawerOpen(false)} aria-label={t('common.close') || 'Close'} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', marginBottom: 16 }}>{t('attendance.history_subtitle') || t('attendance.history')}</p>
            <div>
              <LanguageSelector style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 10px', borderRadius: 10 }} />
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal — filters in body above history on all screen sizes */}
      <div className="history-content" style={{
        paddingTop: isMobile ? '88px' : '110px',
        paddingBottom: isMobile ? '32px' : '40px',
        paddingLeft: isMobile ? '16px' : 'clamp(16px, 4vw, 40px)',
        paddingRight: isMobile ? '16px' : 'clamp(16px, 4vw, 40px)',
        maxWidth: isMobile ? '100%' : '1200px',
        width: isMobile ? '100%' : undefined,
        margin: '0 auto',
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {isMobile && (
          <>
            <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', margin: '0 0 14px 0', fontWeight: 500 }}>
              {t('attendance.history_subtitle') || t('attendance.history')}
            </p>
            <div style={{
              background: 'rgba(0, 212, 255, 0.12)',
              border: '1px solid rgba(0, 212, 255, 0.35)',
              borderRadius: '12px',
              padding: '10px 12px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              alignItems: 'end',
              gap: '8px',
              marginBottom: '20px',
              boxSizing: 'border-box'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <label style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{t('attendance.filter_date') || 'Date'}</label>
                <input type="date" className="history-filter-date-input" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '13px', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <label style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }} translate="no">{t('attendance.filter_type') || 'Type'}</label>
                <select value={filterType} aria-label="Type" onChange={(e) => setFilterType(e.target.value)} style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '13px', width: '100%', cursor: 'pointer', boxSizing: 'border-box' }}>
                  <option value="all">{t('attendance.filter_all') || 'All'}</option>
                  <option value="in">{t('attendance.arrival') || 'Arrival'}</option>
                  <option value="out">{t('attendance.departure') || 'Departure'}</option>
                </select>
              </div>
              <button onClick={loadHistory} disabled={loading} style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #00d4ff, #00a8cc)', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: loading ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                {t('attendance.filter_apply') || 'Apply'}
              </button>
            </div>
          </>
        )}
        {!isMobile && (
          <div style={{
            background: 'rgba(0, 212, 255, 0.12)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0, 212, 255, 0.35)',
            borderRadius: '16px',
            padding: '16px 20px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: '14px',
            marginBottom: '24px',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>{t('attendance.filter_date') || 'Date'}</label>
              <input type="date" className="history-filter-date-input" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '14px', minWidth: '160px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }} translate="no">{t('attendance.filter_type') || 'Type'}</label>
              <select value={filterType} aria-label="Type" onChange={(e) => setFilterType(e.target.value)} style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: '#fff', fontSize: '14px', minWidth: '160px', cursor: 'pointer', boxSizing: 'border-box' }}>
                <option value="all">{t('attendance.filter_all') || 'All'}</option>
                <option value="in">{t('attendance.arrival') || 'Arrival'}</option>
                <option value="out">{t('attendance.departure') || 'Departure'}</option>
              </select>
            </div>
            <button onClick={loadHistory} disabled={loading} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #00d4ff, #00a8cc)', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.7 : 1 }}>
              <RefreshCw size={16} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
              {t('attendance.filter_apply') || 'Apply'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '16px'
          }}>
            <RefreshCw size={32} style={{ margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
            {t('common.loading') || 'Chargement...'}
          </div>
        ) : history.length === 0 ? (
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            padding: '60px 40px',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <History size={64} style={{ margin: '0 auto 24px', opacity: 0.5 }} />
            <h3 style={{
              fontSize: '24px',
              fontWeight: 700,
              marginBottom: '12px',
              color: '#ffffff'
            }}>
              {t('attendance.no_records') || 'Aucun pointage enregistré'}
            </h3>
            <p style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '32px'
            }}>
              {t('attendance.no_records_desc') || 'Vos pointages apparaîtront ici une fois que vous aurez commencé à pointer.'}
            </p>
            <button
              onClick={loadHistory}
              style={{
                background: 'linear-gradient(135deg, #00d4ff, #00a8cc)',
                color: '#ffffff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 212, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <RefreshCw size={18} />
              {t('common.refresh') || 'Actualiser'}
            </button>
          </div>
        ) : (
          <>
            {/* Bouton Actualiser */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '24px'
            }}>
              <button
                onClick={loadHistory}
                disabled={loading}
                style={{
                  background: loading ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s',
                  opacity: loading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
              >
                <RefreshCw size={16} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                {t('common.refresh') || 'Actualiser'}
              </button>
            </div>

            {/* Liste des pointages groupés par date */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              {displayData.map((group) => {
                const dateKey = new Date(group.date).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
                return (
                  <div
                    key={group.date}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '20px',
                      paddingBottom: '16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <Calendar size={20} color="#00d4ff" />
                      <h3 style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#ffffff',
                        margin: 0
                      }}>
                        {dateKey}
                      </h3>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {group.records.map((record, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            padding: '16px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                            e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          }}
                        >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '16px',
                          flexWrap: 'wrap'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flex: 1
                          }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '10px',
                              background: record.type === 'in' 
                                ? 'linear-gradient(135deg, #10b981, #059669)'
                                : 'linear-gradient(135deg, #ef4444, #dc2626)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <Clock size={20} color="#ffffff" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#ffffff',
                                marginBottom: '4px'
                              }}>
                                {(() => {
                                  const raw = record.timestamp || record.date;
                                  if (!raw) return '--';
                                  const s = typeof raw === 'string' && !/Z|[+-]\d{2}:?\d{2}$/.test(raw.trim()) ? raw.trim().replace(' ', 'T') + 'Z' : raw;
                                  return new Date(s).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                                })()}
                              </div>
                              <div style={{
                                fontSize: '14px',
                                color: 'rgba(255, 255, 255, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                flexWrap: 'wrap'
                              }}>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  background: record.type === 'in' 
                                    ? 'rgba(16, 185, 129, 0.2)'
                                    : 'rgba(239, 68, 68, 0.2)',
                                  color: record.type === 'in' ? '#34d399' : '#fca5a5',
                                  fontSize: '12px',
                                  fontWeight: 600
                                }}>
                                  {record.type === 'in' ? (t('attendance.arrival') || 'Arrivée') : (t('attendance.departure') || 'Départ')}
                                </span>
                                {/* Mode de pointage (Facial/Manuel/GPS) */}
                                {record.mode && (
                                  <span style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    background: record.mode === 'facial' 
                                      ? 'rgba(59, 130, 246, 0.2)'
                                      : record.mode === 'manual'
                                      ? 'rgba(251, 191, 36, 0.2)'
                                      : 'rgba(34, 197, 94, 0.2)',
                                    color: record.mode === 'facial'
                                      ? '#60a5fa'
                                      : record.mode === 'manual'
                                      ? '#fbbf24'
                                      : '#4ade80',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    {record.mode === 'facial' && <UserCheck size={12} />}
                                    {record.mode === 'manual' && <AlertTriangle size={12} />}
                                    {record.mode === 'gps' && <MapPin size={12} />}
                                    {record.mode === 'facial' ? (t('attendance.mode_facial') || 'Facial') :
                                     record.mode === 'manual' ? (t('attendance.mode_manual') || 'Manuel') :
                                     (t('attendance.mode_gps') || 'GPS')}
                                  </span>
                                )}
                                {record.locationName && (
                                  <span style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '12px'
                                  }}>
                                    <MapPin size={14} />
                                    {record.locationName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Photo de pointage */}
                        {record.photoPath && (
                          <div style={{
                            marginTop: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <img
                              src={`${API_BASE}${record.photoPath}`}
                              alt={t('attendance.photo_available') || 'Photo de pointage'}
                              onClick={() => setSelectedPhoto(`${API_BASE}${record.photoPath}`)}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.5)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#ffffff',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                <Camera size={14} />
                                {t('attendance.photo_available') || 'Photo disponible'}
                              </div>
                              {record.photoTakenAt && (
                                <div style={{
                                  fontSize: '12px',
                                  color: 'rgba(255, 255, 255, 0.6)'
                                }}>
                                  {new Date(record.photoTakenAt).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {record.notes && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            lineHeight: '1.5'
                          }}>
                            {record.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modal Photo Agrandie */}
      {selectedPhoto && (
        <div
          onClick={() => setSelectedPhoto(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            cursor: 'pointer'
          }}
        >
          <div style={{
            position: 'relative',
            maxWidth: '90%',
            maxHeight: '90%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={selectedPhoto}
              alt={t('attendance.photo_available') || 'Photo de pointage'}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.8)'
              }}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhoto(null);
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '24px',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('attendance.close_photo') || 'Fermer'}
            >
              <X size={24} />
            </button>
          </div>
        </div>
      )}

      {/* CSS pour animations et responsive */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .history-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .history-content {
            padding: 180px 60px 60px !important;
          }
        }
        .history-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

/**
 * Wrapper pour les composants qui utilisent onNavigate
 */
const withNavigation = (Component) => {
  return (props) => {
    const navigate = useNavigate();
    const navigateWrapper = createNavigateWrapper(navigate);
    return <Component {...props} onNavigate={navigateWrapper} />;
  };
};

// Wrapper tous les composants qui utilisent onNavigate
const HomePageWithNav = withNavigation(HomePage);
const GeolocationAttendanceWithNav = withNavigation(GeolocationAttendance);
const GeolocationAdminPanelWithNav = withNavigation(GeolocationAdminPanel);
const AttendanceAuditWithNav = withNavigation(AttendanceAudit);
const AttendanceHistoryWithNav = withNavigation(AttendanceHistory);
const AdminDashboardWithNav = withNavigation(AdminDashboard);
const EmployeesWithNav = withNavigation(Employees);
const EmployeesImportWithNav = withNavigation(EmployeesImport);
const ReportGeneratorWithNav = withNavigation(ReportGenerator);
const LeavesManagementWithNav = withNavigation(LeavesManagement);
const BalanceManagementWithNav = withNavigation(BalanceManagement);
const BadgeManagementWithNav = withNavigation(BadgeManagement);
const FormationsManagementWithNav = withNavigation(FormationsManagement);
const NotesFraisManagementWithNav = withNavigation(NotesFraisManagement);
const PayrollRunsWithNav = withNavigation(PayrollRuns);
const PerformanceDashboardWithNav = withNavigation(PerformanceDashboard);
const PersonalCalendarWithNav = withNavigation(PersonalCalendar);
const TeamCalendarWithNav = withNavigation(TeamCalendar);
const IdeasListWithNav = withNavigation(IdeasList);
const IdeasNewWithNav = withNavigation(IdeasNew);
const IdeasDetailWithNav = withNavigation(IdeasDetail);
const AnnouncementsManagerWithNav = withNavigation(AnnouncementsManager);
const BillingDashboardWithNav = withNavigation(BillingDashboard);
const BillingPlansWithNav = withNavigation(BillingPlans);
const BillingSuccessWithNav = withNavigation(BillingSuccess);
const UserProfileWithNav = withNavigation(UserProfile);
const HelpPageWithNav = withNavigation(HelpPage);
const DocumentsPageWithNav = withNavigation(DocumentsPage);
const AdminPanelWithNav = withNavigation(AdminPanel);
const AdminDashboardHomeWithNav = withNavigation(AdminDashboardHome);
const SuperAdminPanelWithNav = withNavigation(SuperAdminPanel);
const LiveChatWithNav = withNavigation(LiveChat);
const NotificationCenterWithNav = withNavigation(NotificationCenter);

/**
 * Composant qui gère toutes les routes de l'application
 */
const AppRoutes = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [enabledFeatures, setEnabledFeatures] = React.useState([]);
  const [currentPlan, setCurrentPlan] = React.useState(null);
  const [billingLoaded, setBillingLoaded] = React.useState(false);
  const [billingData, setBillingData] = React.useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = React.useState(false);

  const fetchBillingFeatures = React.useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetchWithTimeout(
        `${API_BASE}/api/billing/status`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } },
        25000
      );
      if (response.ok) {
        const data = await response.json();
        setBillingData(data);
        setEnabledFeatures(data.features || []);
        setCurrentPlan(data.plan || 'starter');
        setSubscriptionExpired(!!data.subscriptionExpired);
      } else {
        const err = await response.json().catch(() => ({}));
        if (err.code === 'SUBSCRIPTION_EXPIRED') setSubscriptionExpired(true);
        setEnabledFeatures([]);
        setBillingData(null);
      }
      setBillingLoaded(true);
    } catch (error) {
      const isTimeout = error?.name === 'AbortError';
      if (isTimeout) {
        console.warn('⚠️ Billing status: request timed out (backend may be slow or unreachable)');
      } else {
        console.error('❌ Erreur récupération features:', error);
      }
      setBillingLoaded(true);
      setBillingData(null);
    }
  }, [user]);

  React.useEffect(() => {
    if (user) fetchBillingFeatures();
  }, [user, fetchBillingFeatures]);

  // Refetch when navigating to home/dashboard/billing screens (company subscription → modules)
  // Include all /facturation/* (plans, success) so after plan purchase the trial-ended screen disappears
  React.useEffect(() => {
    const refreshPaths = ['/home', '/admin/dashboard', '/dashboard'];
    const isBillingPath = location.pathname === '/facturation' || location.pathname.startsWith('/facturation/');
    if (user && (refreshPaths.includes(location.pathname) || isBillingPath)) {
      fetchBillingFeatures();
    }
  }, [user, location.pathname, fetchBillingFeatures]);

  // Refetch on window focus (e.g. return from Stripe / Plans page after purchase)
  // Only refetch when on billing-related pages to avoid redundant requests
  React.useEffect(() => {
    const onFocus = () => {
      if (!user) return;
      if (location.pathname.startsWith('/facturation')) {
        fetchBillingFeatures();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, fetchBillingFeatures, location.pathname]);

  return (
    <Routes>
      {/* Route par défaut: rediriger vers /home */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      
      {/* Routes avec layout Dashboard - enabledFeatures + subscription expiry */}
      <Route 
        element={
          <ProtectedRoute>
            <DashboardLayout 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan} 
              subscriptionExpired={subscriptionExpired}
              billingLoaded={billingLoaded}
              billingData={billingData}
              onRefetchBilling={fetchBillingFeatures}
            />
          </ProtectedRoute>
        }
      >
        {/* Route /home */}
        <Route 
          path="/home" 
          element={<HomePageWithNav user={user} onLogout={logout} />}
        />
      
      {/* Routes publiques (authentification) */}
      <Route path="/login" element={<Navigate to="/?auth=true" replace />} />
      
        {/* Routes protégées par feature */}
        <Route 
          path="/pointage" 
          element={
            <ProtectedRoute requiredFeature="pointage" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <GeolocationAttendanceWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/historique" 
          element={
            <ProtectedRoute requiredFeature="historique" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <AttendanceHistoryWithNav />
            </ProtectedRoute>
          } 
        />
      
        <Route 
          path="/calendrier" 
          element={
            <ProtectedRoute requiredFeature="calendrier" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <PersonalCalendarWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/planning" 
          element={
            <ProtectedRoute requiredFeature="planning" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <TeamCalendarWithNav user={user} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/ideas/new" 
          element={
            <ProtectedRoute>
              <IdeasNewWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/ideas/:id" 
          element={
            <ProtectedRoute>
              <IdeasDetailWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/ideas" 
          element={
            <ProtectedRoute>
              <IdeasListWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/communiques" 
          element={
            <ProtectedRoute requiredFeature="communiques" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <AnnouncementsManagerWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/conges" 
          element={
            <ProtectedRoute requiredFeature="conges" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <LeavesManagementWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/formations" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager', 'employee']} 
              requiredFeature="formations" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <FormationsManagementWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/notes-frais" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager', 'employee']} 
              requiredFeature="notes_frais" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <NotesFraisManagementWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/performance" 
          element={
            <ProtectedRoute requiredFeature="performance" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <PerformanceDashboardWithNav user={user} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/live-chat" 
          element={
            <ProtectedRoute requiredFeature="live_chat" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <LiveChatWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/documents" 
          element={
            <ProtectedRoute requiredFeature="documents" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <DocumentsPageWithNav user={user} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profil" 
          element={
            <ProtectedRoute requiredFeature="profil" enabledFeatures={enabledFeatures} currentPlan={currentPlan}>
              <UserProfileWithNav user={user} />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<div style={{ padding: 24, color: '#fff', textAlign: 'center' }}>Loading...</div>}>
                <NotificationCenterWithNav />
              </Suspense>
            </ProtectedRoute>
          } 
        />

        <Route
          path="/push-debug"
          element={
            <ProtectedRoute>
              <PushDebugPage />
            </ProtectedRoute>
          }
        />
        
        <Route 
          path="/aide" 
          element={
            <ProtectedRoute>
              <HelpPageWithNav />
            </ProtectedRoute>
          } 
        />
        
        {/* Routes admin/manager */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager', 'employee']} 
              requiredFeature="dashboard" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <AdminDashboardWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/employees" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager']} 
              requiredFeature="employees" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <EmployeesWithNav />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employees/import" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager']} 
              requiredFeature="employees" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <EmployeesImportWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/rapports" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager']} 
              requiredFeature="reports" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <ReportGeneratorWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/badges" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager']} 
              requiredFeature="badges" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <BadgeManagementWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/soldes" 
          element={
            <ProtectedRoute 
              requiredRole="admin" 
              requiredFeature="soldes" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <BalanceManagementWithNav user={user} />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/paie" 
          element={
            <ProtectedRoute 
              requiredRoles={['admin', 'manager']} 
              requiredFeature="payroll" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <PayrollRunsWithNav />
            </ProtectedRoute>
          } 
        />
        
        {/* Routes admin uniquement */}
        <Route 
          path="/admin/gps" 
          element={
            <ProtectedRoute 
              requiredRole="admin" 
              requiredFeature="pointage" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <GeolocationAdminPanelWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/audit-photos" 
          element={
            <ProtectedRoute 
              requiredRole="admin" 
              requiredFeature="audit_photos" 
              enabledFeatures={enabledFeatures} 
              currentPlan={currentPlan}
            >
              <AttendanceAuditWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminPanelWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']}>
              <AdminDashboardHomeWithNav user={user} onLogout={logout} />
            </ProtectedRoute>
          } 
        />
        
        {/* Routes facturation – admin + manager so either can renew when trial expires */}
        <Route 
          path="/facturation" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']} deniedSection="billing">
              <BillingDashboardWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/facturation/plans" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']} deniedSection="billing">
              <BillingPlansWithNav />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/facturation/success" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'manager']} deniedSection="billing">
              <BillingSuccessWithNav />
            </ProtectedRoute>
          } 
        />
        
        {/* Route Super Admin */}
        <Route 
          path="/super-admin" 
          element={
            ((user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin') ? (
              <SuperAdminPanelWithNav />
            ) : (
              <Navigate to="/home" replace />
            )
          } 
        />
      </Route>
      
      {/* Route 404 - rediriger vers /home */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

export default AppRoutes;

